import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, gql } from '@apollo/client';
import { useAuth } from './AuthContext';
import './office_panel.css';
import './principal.css';
import { EtiquetaDashboard, ESTADO_DASHBOARD } from './dashboardNawaiam';

// ─── Queries & Mutations ─────────────────────────────────────────────────────

const GET_USER_EMPRESA = gql`
  query GetUserEmpresa($id: ID!) {
    getUserEmpresaById(id: $id) { id empresaKey username idUseradmin email }
  }
`;

const GET_EXTERNAL_PLAYERS = gql`
  query GetPlayers($companyName: String!) {
    getExternalPlayers(companyName: $companyName) { ID Nombre Apellido Mail Perfil }
  }
`;

const FETCH_USERCONDUCTUAL_LINK_QUERY = gql`
  query($username: String!) {
    getUsersLinkByUsername(username: $username) { id ides nombres dob email emailValidacion disc informacion createdAt }
  }
`;

const CREATE_USER_EMPRESA_NUEVA = gql`
  mutation CreateUserEmpresaNueva($adminUsername: String!, $email: String!, $empresaKey: String!, $players: [PlayerInput]!) {
    createUserEmpresaNueva(adminUsername: $adminUsername, email: $email, empresaKey: $empresaKey, players: $players) { id username }
  }
`;

const UPDATE_USER_EMPRESA = gql`
  mutation UpdateUserEmpresa($username: String!, $players: [PlayerInput]!) {
    updateUserEmpresa(username: $username, players: $players) { id username }
  }
`;

const DEACTIVATE_USER_LINK = gql`mutation DeactivateUserLink($id: ID!) { deactivateUserLink(id: $id) }`;

const CREATE_MANUAL_PLAYER = gql`
  mutation CreateManualPlayer($empresaId: ID!, $nombre: String!, $apellido: String!, $email: String, $dob: String) {
    createManualPlayer(empresaId: $empresaId, nombre: $nombre, apellido: $apellido, email: $email, dob: $dob) { id nombres email dob disc }
  }
`;
// Email de las personas cargadas a mano (desde el 25/10/2026): el codigo le llega a la persona, que lo confirma en
// /validar-email o se lo dicta a quien la cargo.
const VALIDAR_CODIGO_PERSONA = gql`mutation VCP($id: ID!, $codigo: String!) { validarCodigoPersona(id: $id, codigo: $codigo) }`;
const REENVIAR_CODIGO_PERSONA = gql`mutation RCP($id: ID!) { reenviarCodigoPersona(id: $id) }`;

// ─── Component ───────────────────────────────────────────────────────────────

export default function UserPlayerPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user: authUser, isSuperadmin } = useAuth();
  const isAdminOrSuper = ['admin', 'superadmin'].includes(authUser?.role);

  // State
  const [panelList, setPanelList] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [panelSearchTerm, setPanelSearchTerm] = useState('');
  const [toast, setToast] = useState({ show: false, message: '', type: 'success' });
  const [confirmationMode, setConfirmationMode] = useState(null);
  const [deleteConfirmationId, setDeleteConfirmationId] = useState(null);
  const [codigoPara, setCodigoPara] = useState(null);   // id de la persona a la que se le escribe el codigo
  const [codigoPersona, setCodigoPersona] = useState('');
  const [showManualAdd, setShowManualAdd] = useState(false);
  const [manualNombre, setManualNombre] = useState('');
  const [manualApellido, setManualApellido] = useState('');
  const [manualEmail, setManualEmail] = useState('');
  const [manualDob, setManualDob] = useState('');
  const [activeFilter, setActiveFilter] = useState('ALL');
  const [activeApiFilter, setActiveApiFilter] = useState('ALL');

  // Queries
  const { data: userData, loading: userLoading } = useQuery(GET_USER_EMPRESA, { variables: { id } });
  const empresaKey = userData?.getUserEmpresaById?.empresaKey;
  const adminUsername = userData?.getUserEmpresaById?.username;
  const userEmail = userData?.getUserEmpresaById?.email;
  const headerIdUserAdmin = userData?.getUserEmpresaById?.idUseradmin;

  const { data: playersData, loading: playersLoading } = useQuery(GET_EXTERNAL_PLAYERS, {
    variables: { companyName: empresaKey }, skip: !empresaKey
  });
  // ¿Existe ese dashboard en Nawaiam? Si no, la cuenta no tiene de dónde traer players y hay que decirlo claro.
  const { data: estadoData } = useQuery(ESTADO_DASHBOARD, { variables: { companyName: empresaKey }, skip: !empresaKey });
  const estadoDash = estadoData?.estadoDashboardNawaiam || null;

  const { data: userConductualLinkData, loading: linkLoading, refetch: refetchLinks } = useQuery(FETCH_USERCONDUCTUAL_LINK_QUERY, {
    variables: { username: adminUsername }, skip: !adminUsername
  });

  const mapDbUsersToPanel = (dbUsers) => dbUsers.map(u => ({
    ID: u.id, Nombre: u.nombres, Apellido: '', Mail: u.email, Perfil: u.disc,
    Informacion: u.informacion || '', Ides: u.ides || [], Dob: u.dob, EmailValidacion: u.emailValidacion || null, isNew: false
  }));

  const flash = (msg, type = 'success') => {
    setToast({ show: true, message: msg, type });
    setTimeout(() => setToast(prev => ({ ...prev, show: false })), 4000);
  };

  // Mutations
  const [createUserEmpresaNueva, { loading: creatingUser }] = useMutation(CREATE_USER_EMPRESA_NUEVA, {
    onCompleted: async () => { flash('Usuario creado.'); setConfirmationMode(null); const { data } = await refetchLinks(); if (data?.getUsersLinkByUsername) setPanelList(mapDbUsersToPanel(data.getUsersLinkByUsername)); },
    onError: (err) => { flash(err.message, 'error'); setConfirmationMode(null); }
  });

  const [updateUserEmpresa, { loading: updatingUser }] = useMutation(UPDATE_USER_EMPRESA, {
    onCompleted: async () => { flash('Lista actualizada.'); setConfirmationMode(null); const { data } = await refetchLinks(); if (data?.getUsersLinkByUsername) setPanelList(mapDbUsersToPanel(data.getUsersLinkByUsername)); },
    onError: (err) => { flash(err.message, 'error'); setConfirmationMode(null); }
  });

  const [createManualPlayer, { loading: creatingManual }] = useMutation(CREATE_MANUAL_PLAYER, {
    onCompleted: async () => {
      flash(`Usuario manual agregado. Le mandamos un código a ${manualEmail.trim()} para validar su email.`);
      setShowManualAdd(false); setManualNombre(''); setManualApellido(''); setManualEmail(''); setManualDob('');
      const { data } = await refetchLinks();
      if (data?.getUsersLinkByUsername) setPanelList(mapDbUsersToPanel(data.getUsersLinkByUsername));
    },
    onError: (err) => flash(err.message, 'error')
  });

  const recargar = async () => { const { data } = await refetchLinks(); if (data?.getUsersLinkByUsername) setPanelList(mapDbUsersToPanel(data.getUsersLinkByUsername)); };
  const [validarCodigoPersona, { loading: validandoCodigo }] = useMutation(VALIDAR_CODIGO_PERSONA, {
    onCompleted: async () => { flash('Email validado.'); setCodigoPara(null); setCodigoPersona(''); await recargar(); },
    onError: (err) => flash(err.message, 'error')
  });
  const [reenviarCodigoPersona] = useMutation(REENVIAR_CODIGO_PERSONA, {
    onCompleted: () => flash('Le mandamos un código nuevo.'),
    onError: (err) => flash(err.message, 'error')
  });

  const [deactivateUserLink] = useMutation(DEACTIVATE_USER_LINK, {
    onCompleted: async () => { flash('Colaborador desactivado.'); const { data } = await refetchLinks(); if (data?.getUsersLinkByUsername) setPanelList(mapDbUsersToPanel(data.getUsersLinkByUsername)); },
    onError: (err) => flash(err.message, 'error')
  });

  useEffect(() => {
    if (userConductualLinkData?.getUsersLinkByUsername) setPanelList(mapDbUsersToPanel(userConductualLinkData.getUsersLinkByUsername));
  }, [userConductualLinkData]);

  // Handlers
  const addToPanel = (p) => { if (!panelList.find(x => (x.Mail || '').toLowerCase() === (p.Mail || '').toLowerCase())) setPanelList([...panelList, { ...p, isNew: true }]); };
  const removeFromPanel = (pid) => setPanelList(panelList.filter(p => p.ID !== pid));

  const executeCreate = () => {
    const players = panelList.map(p => ({ Nombre: p.Nombre, Apellido: p.Apellido || '', Mail: p.Mail, Perfil: p.Perfil, Ides: p.Ides || [] }));
    createUserEmpresaNueva({ variables: { adminUsername: headerIdUserAdmin, email: userEmail, empresaKey, players } });
  };
  const executeUpdate = () => {
    const players = panelList.map(p => ({ Nombre: p.Nombre, Apellido: p.Apellido || '', Mail: p.Mail, Perfil: p.Perfil, Ides: p.Ides || [] }));
    updateUserEmpresa({ variables: { username: adminUsername, players } });
  };

  const confirmDeleteUser = (pid) => { setPanelList(prev => prev.filter(p => p.ID !== pid)); deactivateUserLink({ variables: { id: pid } }); setDeleteConfirmationId(null); };

  // Detectar removidos (items en initial que ya no están en panelList) para confirm en UPDATE
  const initialList = userConductualLinkData?.getUsersLinkByUsername || [];
  const currentIds = new Set(panelList.map(p => p.ID));
  const removedItems = initialList.filter(u => !currentIds.has(u.id));

  // Filters — API (left)
  const allPlayers = playersData?.getExternalPlayers || [];
  const apiFiltered = allPlayers.filter(p => {
    const term = searchTerm.toLowerCase();
    return (p.Nombre + ' ' + p.Apellido).toLowerCase().includes(term) || (p.Mail || '').toLowerCase().includes(term);
  });
  const countApiAdded = apiFiltered.filter(p => panelList.some(x => (x.Mail || '').toLowerCase() === (p.Mail || '').toLowerCase())).length;
  const apiDisplay = apiFiltered.filter(p => {
    const isAdded = panelList.some(x => (x.Mail || '').toLowerCase() === (p.Mail || '').toLowerCase());
    if (activeApiFilter === 'AVAILABLE') return !isAdded;
    if (activeApiFilter === 'ADDED') return isAdded;
    return true;
  });

  // Filters — Panel (right)
  const panelFiltered = panelList.filter(p => {
    const term = panelSearchTerm.toLowerCase();
    return (p.Nombre || '').toLowerCase().includes(term) || (p.Mail || '').toLowerCase().includes(term) || (p.Informacion || '').toLowerCase().includes(term);
  });
  const countActivos = panelFiltered.filter(p => p.Dob && p.Dob !== 'pendiente').length;
  const countEquipo = panelFiltered.filter(p => Array.isArray(p.Ides) && p.Ides.length >= 2).length;
  const countNuevos = panelFiltered.filter(p => p.isNew).length;
  const countSinFecha = panelFiltered.filter(p => p.Dob === 'pendiente').length;
  const panelDisplay = panelFiltered.filter(p => {
    if (activeFilter === 'ACTIVOS') return p.Dob && p.Dob !== 'pendiente';
    if (activeFilter === 'EQUIPO') return Array.isArray(p.Ides) && p.Ides.length >= 2;
    if (activeFilter === 'NUEVOS') return p.isNew;
    if (activeFilter === 'SIN_FECHA') return p.Dob === 'pendiente';
    return true;
  });

  // Status badge
  let statusTag = null;
  if (adminUsername === 'pendiente') statusTag = <span className="tag tag--yellow">Nuevo</span>;
  else if (adminUsername && headerIdUserAdmin && adminUsername !== headerIdUserAdmin) statusTag = <span className="tag tag--green">Activa</span>;
  else if (adminUsername && headerIdUserAdmin && adminUsername === headerIdUserAdmin) statusTag = <span className="tag tag--red">Admin</span>;

  // User empresa created = can interact with players
  const isUserCreated = adminUsername && adminUsername !== 'pendiente';

  if (userLoading) return <div style={{ padding: 40, textAlign: 'center' }}><div className="spinner" /></div>;

  return (
    <div className="page-container">

      {/* Toast */}
      <div className={`toast toast--${toast.type === 'error' ? 'error' : 'success'} ${toast.show ? 'toast--show' : ''}`}>
        {toast.type === 'error' ? '⚠️' : '✅'} {toast.message}
      </div>

      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 6, flexWrap: 'wrap' }}>
        <button className="btn-subtle" onClick={() => navigate('/altaEmpresa')}>← Volver</button>
        <h1 style={{ fontSize: '1.4rem', fontWeight: 700, letterSpacing: '-0.4px', margin: 0 }}>{empresaKey}</h1>
        {statusTag}
      </div>
      <div className="op-fila-dash" style={{ marginBottom: 8 }}><EtiquetaDashboard nombre={empresaKey} /></div>
      {estadoDash && !estadoDash.sinDashboard && !estadoDash.existe && !estadoDash.error && (
        <div className="op-aviso op-aviso--error" style={{ marginBottom: 14 }}>
          Nawaiam no encuentra un dashboard llamado «{empresaKey}»: esta cuenta no tiene de dónde traer players. Revisá el nombre exacto del dashboard en Nawaiam.
        </div>
      )}
      {estadoDash?.error && (
        <div className="op-aviso op-aviso--alerta" style={{ marginBottom: 14 }}>No pude verificar el dashboard en Nawaiam ({estadoDash.error}).</div>
      )}
      <p style={{ color: '#9ca3af', fontSize: '0.84rem', marginBottom: 20 }}>
        Seleccioná usuarios de Nawaiam para moverlos al Panel Nawi.{estadoDash?.existe ? ` El dashboard devuelve ${estadoDash.personas} players.` : ''}
      </p>

      {/* ═══ Two columns ═══ */}
      <div className="players-grid">

        {/* ─── LEFT: Nawaiam Users ─── */}
        <div className="panel-card" style={{ padding: 20, opacity: isUserCreated ? 1 : 0.4, pointerEvents: isUserCreated ? 'auto' : 'none', position: 'relative' }}>
          {!isUserCreated && (
            <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2, pointerEvents: 'auto', background: 'rgba(255,255,255,0.6)', borderRadius: 14 }}>
              <div style={{ textAlign: 'center', padding: '20px 24px', background: '#fff', borderRadius: 12, boxShadow: '0 4px 20px rgba(0,0,0,0.08)', maxWidth: 260 }}>
                <div style={{ fontSize: 28, marginBottom: 8 }}>🔒</div>
                <p style={{ fontSize: '0.88rem', fontWeight: 600, color: '#1a1a2e', margin: '0 0 4px' }}>Panel bloqueado</p>
                <p style={{ fontSize: '0.78rem', color: '#9ca3af', margin: 0 }}>Primero creá el usuario empresa desde el panel derecho.</p>
              </div>
            </div>
          )}
          <div className="section-head">
            <span className="section-title">Usuarios Nawaiam</span>
            <span className="section-count">{apiFiltered.length}</span>
          </div>
          <div className="filter-row">
            <span className={`tag tag--blue tag--clickable ${activeApiFilter === 'AVAILABLE' ? '' : 'tag--dim'}`} onClick={() => setActiveApiFilter(activeApiFilter === 'AVAILABLE' ? 'ALL' : 'AVAILABLE')}>{apiFiltered.length - countApiAdded} Disponibles</span>
            <span className={`tag tag--green tag--clickable ${activeApiFilter === 'ADDED' ? '' : 'tag--dim'}`} onClick={() => setActiveApiFilter(activeApiFilter === 'ADDED' ? 'ALL' : 'ADDED')}>{countApiAdded} Agregados</span>
          </div>
          <input className="search-bar" placeholder="Buscar por nombre o email..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} style={{ marginBottom: 10 }} />

          <div className="player-scroll">
            {playersLoading ? <div className="spinner" /> : apiDisplay.length === 0 ? (
              <div className="empty-state">{allPlayers.length === 0 ? 'Nawaiam no devolvió players para este dashboard.' : `Sin resultados para "${searchTerm}"`}</div>
            ) : apiDisplay.map((p, i) => {
              const isAdded = panelList.some(x => (x.Mail || '').toLowerCase() === (p.Mail || '').toLowerCase());
              return (
                <div key={`${p.ID}-${i}`} className={`player-item ${isAdded ? 'player-item--disabled' : ''}`} onClick={() => !isAdded && addToPanel(p)}>
                  <div className="player-info">
                    <div className="player-name">{p.Nombre} {p.Apellido} {isAdded ? <span className="tag tag--green">Agregado</span> : <span className="tag tag--blue">Disponible</span>}</div>
                    <div className="player-meta">{p.Mail}</div>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <span className="player-disc">{p.Perfil}</span>
                    {isAdded ? <span className="player-check">✓</span> : <span className="player-add">+</span>}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* ─── RIGHT: Panel Nawi ─── */}
        <div className="panel-card" style={{ display: 'flex', flexDirection: 'column' }}>
          <div style={{ padding: 20, paddingBottom: 0 }}>
            <div className="section-head">
              <span className="section-title section-title--accent">Panel Nawi</span>
              <span className="section-count">{panelFiltered.length}</span>
            </div>
            <div className="filter-row">
              <span className={`tag tag--green tag--clickable ${activeFilter === 'ACTIVOS' ? '' : 'tag--dim'}`} onClick={() => setActiveFilter(activeFilter === 'ACTIVOS' ? 'ALL' : 'ACTIVOS')}>{countActivos} Activos</span>
              <span className={`tag tag--blue tag--clickable ${activeFilter === 'EQUIPO' ? '' : 'tag--dim'}`} onClick={() => setActiveFilter(activeFilter === 'EQUIPO' ? 'ALL' : 'EQUIPO')}>{countEquipo} Equipo</span>
              <span className={`tag tag--purple tag--clickable ${activeFilter === 'NUEVOS' ? '' : 'tag--dim'}`} onClick={() => setActiveFilter(activeFilter === 'NUEVOS' ? 'ALL' : 'NUEVOS')}>{countNuevos} Nuevos</span>
              <span className={`tag tag--yellow tag--clickable ${activeFilter === 'SIN_FECHA' ? '' : 'tag--dim'}`} onClick={() => setActiveFilter(activeFilter === 'SIN_FECHA' ? 'ALL' : 'SIN_FECHA')}>{countSinFecha} Sin fecha</span>
            </div>
            <input className="search-bar" placeholder="Filtrar panel..." value={panelSearchTerm} onChange={e => setPanelSearchTerm(e.target.value)} style={{ marginBottom: 10 }} />
          </div>

          <div className="player-scroll" style={{ padding: '0 20px' }}>
            {linkLoading ? <div className="spinner" /> : panelList.length === 0 ? (
              <div className="empty-state">El panel está vacío. Agregá usuarios desde la izquierda.</div>
            ) : panelDisplay.length === 0 ? (
              <div className="empty-state">Sin resultados para el filtro actual.</div>
            ) : panelDisplay.map(p => {
              const isInNawaiam = allPlayers.some(ap => (ap.Mail || '').toLowerCase() === (p.Mail || '').toLowerCase());
              const isTeam = Array.isArray(p.Ides) && p.Ides.length >= 2;

              if (deleteConfirmationId === p.ID) {
                return (
                  <div key={p.ID} className="del-confirm" style={{ padding: 14, background: '#fff7ed', border: '1px solid #fed7aa', borderRadius: 10 }}>
                    <p style={{ fontWeight: 700, marginBottom: 8 }}>Desactivar a "{p.Nombre} {p.Apellido}"</p>
                    <p style={{ fontSize: '0.85rem', color: '#6b7280', marginBottom: 12, lineHeight: 1.5 }}>
                      Este colaborador desaparece de la lista. Las conversaciones y los perfiles vinculados a él se mantienen como están.
                    </p>
                    <p style={{ fontSize: '0.85rem', color: '#6b7280', marginBottom: 12, lineHeight: 1.5 }}>
                      Podés reactivarlo más adelante.
                    </p>
                    <div className="del-confirm-btns" style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                      <button className="btn-outline" onClick={() => setDeleteConfirmationId(null)}>Cancelar</button>
                      <button
                        onClick={() => confirmDeleteUser(p.ID)}
                        style={{ background: '#f59e0b', color: '#fff', border: 'none', padding: '7px 14px', borderRadius: 8, cursor: 'pointer', fontSize: '0.8rem', fontWeight: 600 }}>
                        Desactivar colaborador
                      </button>
                    </div>
                  </div>
                );
              }

              return (
                <div key={p.ID} className="player-item" style={{ cursor: 'default' }}>
                  <div className="player-info">
                    <div className="player-name">
                      {p.Nombre} {p.Apellido}
                      {!isInNawaiam && <span style={{ fontSize: '0.68rem', color: '#9ca3af' }}>(Externo)</span>}
                      {p.Informacion && <span style={{ fontSize: '0.68rem', color: '#9ca3af' }}>({p.Informacion})</span>}
                      {isTeam && <span className="tag tag--blue">Equipo</span>}
                      {p.isNew && <span className="tag tag--purple">Nuevo</span>}
                      {p.Dob === 'pendiente' ? <span className="tag tag--yellow">Sin fecha</span> : p.Dob && <span className="tag tag--green">Activo</span>}
                      {p.EmailValidacion === 'pendiente' && <span className="tag tag--yellow" title={p.Mail}>Email sin validar</span>}
                    </div>
                    <div className="player-meta">{p.Perfil}</div>
                    {p.EmailValidacion === 'pendiente' && isAdminOrSuper && (
                      codigoPara === p.ID ? (
                        <div className="ev-codigo">
                          <input className="manual-input" inputMode="numeric" maxLength={6} placeholder="6 números" value={codigoPersona}
                            onChange={e => setCodigoPersona(e.target.value.replace(/\D/g, '').slice(0, 6))} autoFocus />
                          <button className="btn-green" disabled={codigoPersona.length !== 6 || validandoCodigo}
                            onClick={() => validarCodigoPersona({ variables: { id: p.ID, codigo: codigoPersona } })}>{validandoCodigo ? 'Validando...' : 'Validar'}</button>
                          <button className="btn-outline" onClick={() => { setCodigoPara(null); setCodigoPersona(''); }}>Cancelar</button>
                        </div>
                      ) : (
                        <div className="ev-acciones">
                          <button className="ev-link" onClick={() => { setCodigoPara(p.ID); setCodigoPersona(''); }}>Ingresar código</button>
                          <span>·</span>
                          <button className="ev-link" onClick={() => reenviarCodigoPersona({ variables: { id: p.ID } })}>Reenviar código</button>
                        </div>
                      )
                    )}
                  </div>
                  {p.isNew ? (
                    isAdminOrSuper && <button className="player-remove" onClick={() => removeFromPanel(p.ID)} title="Quitar">×</button>
                  ) : (
                    isAdminOrSuper && <button className="player-remove" onClick={() => setDeleteConfirmationId(p.ID)} title="Desactivar">×</button>
                  )}
                </div>
              );
            })}
          </div>

          {/* Action bar: create/update + manual add */}
          <div className="action-bar">
            {/* Action buttons */}
            {confirmationMode ? (
              <div className="action-confirm">
                {confirmationMode === 'CREATE' ? (
                  <p>¿Confirmar creación del usuario?</p>
                ) : removedItems.length === 0 ? (
                  <p>¿Confirmar actualización?</p>
                ) : (
                  <div style={{ padding: 14, background: '#fff7ed', border: '1px solid #fed7aa', borderRadius: 10 }}>
                    <p style={{ fontWeight: 700, marginBottom: 8 }}>Confirmar cambios</p>
                    <p style={{ fontSize: '0.85rem', color: '#6b7280', marginBottom: 8, lineHeight: 1.5 }}>
                      Estás por desactivar {removedItems.length} colaborador{removedItems.length === 1 ? '' : 'es'} que sacaste de la lista:
                    </p>
                    <ul style={{ fontSize: '0.85rem', color: '#6b7280', marginBottom: 12, paddingLeft: 20, lineHeight: 1.6 }}>
                      {removedItems.slice(0, 5).map(u => (
                        <li key={u.id}>{u.nombres || u.email}</li>
                      ))}
                      {removedItems.length > 5 && <li>y {removedItems.length - 5} más</li>}
                    </ul>
                    <p style={{ fontSize: '0.85rem', color: '#6b7280', marginBottom: 0, lineHeight: 1.5 }}>
                      Sus conversaciones y perfiles quedan intactos. Podés reactivarlos individualmente más adelante.
                    </p>
                  </div>
                )}
                <div className="action-confirm-btns" style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 8 }}>
                  <button className="btn-outline" onClick={() => setConfirmationMode(null)}>Cancelar</button>
                  {confirmationMode === 'CREATE' ? (
                    <button className="btn-accent" onClick={executeCreate} disabled={creatingUser}>{creatingUser ? 'Creando...' : 'Confirmar'}</button>
                  ) : removedItems.length === 0 ? (
                    <button className="btn-green" onClick={executeUpdate} disabled={updatingUser}>{updatingUser ? 'Actualizando...' : 'Confirmar'}</button>
                  ) : (
                    <button
                      onClick={executeUpdate}
                      disabled={updatingUser}
                      style={{ background: '#f59e0b', color: '#fff', border: 'none', padding: '7px 14px', borderRadius: 8, cursor: 'pointer', fontSize: '0.8rem', fontWeight: 600 }}>
                      {updatingUser ? 'Guardando...' : 'Guardar cambios'}
                    </button>
                  )}
                </div>
              </div>
            ) : (
              <>
                {adminUsername === 'pendiente' && isAdminOrSuper && (
                  <button className="btn-accent" style={{ width: '100%' }} onClick={() => setConfirmationMode('CREATE')}>Crear usuario empresa</button>
                )}
                {isUserCreated && adminUsername !== headerIdUserAdmin && isAdminOrSuper && (
                  <button className="btn-green" style={{ width: '100%' }} onClick={() => setConfirmationMode('UPDATE')}>Actualizar lista</button>
                )}
              </>
            )}

            {/* Manual add (superadmin) */}
            {isSuperadmin && !confirmationMode && isUserCreated && (
              <div style={{ marginTop: 10 }}>
                {!showManualAdd ? (
                  <button className="btn-subtle" style={{ width: '100%', border: '1.5px dashed #d0d3db', borderRadius: 10, padding: '10px', color: '#7a7d8e' }}
                    onClick={() => setShowManualAdd(true)}>+ Agregar usuario manual</button>
                ) : (
                  <div className="fade-in">
                    <h4 style={{ fontSize: '0.88rem', fontWeight: 700, marginBottom: 8, color: '#1a1a2e' }}>Nuevo usuario manual</h4>
                    <input className="manual-input" placeholder="Nombre" value={manualNombre} onChange={e => setManualNombre(e.target.value)} autoFocus />
                    <input className="manual-input" placeholder="Apellido" value={manualApellido} onChange={e => setManualApellido(e.target.value)} />
                    <input className="manual-input" placeholder="Email" type="email" value={manualEmail} onChange={e => setManualEmail(e.target.value)} />
                    <span className="manual-label">Fecha de nacimiento</span>
                    <input className="manual-input" type="date" value={manualDob} onChange={e => setManualDob(e.target.value)} />
                    <div style={{ display: 'flex', gap: 6, marginTop: 4 }}>
                      <button className="btn-outline" style={{ flex: 1 }} onClick={() => setShowManualAdd(false)}>Cancelar</button>
                      <button className="btn-green" style={{ flex: 1 }} disabled={creatingManual || !manualNombre.trim() || !manualApellido.trim() || !manualEmail.trim()}
                        onClick={() => {
                          const dob = manualDob ? manualDob.split('-').reverse().join('') : undefined;
                          createManualPlayer({ variables: { empresaId: id, nombre: manualNombre.trim(), apellido: manualApellido.trim(), email: manualEmail.trim(), dob } });
                        }}>
                        {creatingManual ? 'Agregando...' : 'Agregar'}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}